<template>
  <article class="quote-task-tile">
    <div class="quote-task-tile-image">
      <div class="quote-task-tile-image-frame">
        <img
          class="quote-task-tile-img"
          :src="resolvedImageSrc"
          :alt="title || ''"
        />
      </div>
    </div>
    <div class="quote-task-tile-divider" aria-hidden="true"></div>
    <div class="quote-task-tile-body">
      <div v-if="code" class="quote-task-tile-code">{{ code }}</div>
      <h3 class="quote-task-tile-title">{{ title }}</h3>
      <div class="quote-task-tile-footer">
        <div class="quote-task-tile-price-col" v-if="hasPrice">
          <div class="quote-task-tile-price-line">
            <span class="quote-task-tile-currency">$</span>
            <span class="quote-task-tile-dollars">{{ priceParts.dollars }}</span>
            <span class="quote-task-tile-cents">{{ priceParts.cents }}</span>
          </div>
          <div class="quote-task-tile-inc">inc GST</div>
        </div>
        <button
          type="button"
          class="quote-task-tile-btn"
          :aria-label="addAriaLabel"
          @click="$emit('add')"
        >
          <svg width="24" height="24" viewBox="24 22 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M38 32H48V36H38V46H34V36H24V32H34V22H38V32Z" fill="#4DA1FF"/>
          </svg>
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import FALLBACK_IMAGE from '../../assets/img/no-image-available.jpg'

export default {
  name: 'QuoteTaskTile',
  emits: ['add'],
  props: {
    code: {
      type: [String, Number],
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    imageSrc: {
      type: String,
      default: ''
    },
    /** Catalog sell preview as "123.45" / number; omit or empty to hide price. */
    price: {
      type: [String, Number],
      default: null
    },
    addAriaLabel: {
      type: String,
      default: 'Add'
    }
  },
  computed: {
    resolvedImageSrc: function () {
      if (this.imageSrc && String(this.imageSrc).trim() !== '') {
        return this.imageSrc
      }
      return FALLBACK_IMAGE
    },
    hasPrice: function () {
      if (this.price == null || this.price === '') return false
      const n = parseFloat(this.price)
      return Number.isFinite(n) && n > 0
    },
    priceParts: function () {
      if (!this.hasPrice) return { dollars: '', cents: '' }
      const s = Number(this.price).toFixed(2)
      const dot = s.indexOf('.')
      if (dot === -1) return { dollars: s, cents: '' }
      return { dollars: s.slice(0, dot), cents: s.slice(dot) }
    }
  }
}
</script>

<style>
.quote-task-tile {
  display: flex;
  flex-direction: column;
  background: var(--color-bg-surface);
  border: none;
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  box-shadow: var(--shadow-tile);
}
.quote-task-tile-image {
  position: relative;
  width: 100%;
  height: 200px;
  background: var(--color-very-light-grey);
  flex-shrink: 0;
}
.quote-task-tile-image-frame {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  background-color: var(--color-white);
}
.quote-task-tile-img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}
.quote-task-tile-divider {
  height: 1px;
  background: rgba(240, 243, 247, 1);
  flex-shrink: 0;
}
.quote-task-tile-body {
  padding: 16px 18px 16px 18px;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.quote-task-tile-code {
  font-family: var(--font-regular);
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.04em;
  color: var(--color-mid-grey);
  text-transform: uppercase;
  margin-bottom: 6px;
}
.quote-task-tile-title {
  margin: 0 0 16px 0 !important;
  padding: 0 !important;
  font-size: 16px !important;
  line-height: 20px !important;
  font-weight: 600 !important;
  color: var(--color-text) !important;
  font-family: var(--font-semibold) !important;
}
.quote-task-tile-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: auto;
}
.quote-task-tile-price-col {
  min-width: 0;
  flex: 0 1 auto;
}
.quote-task-tile-price-line {
  font-family: var(--font-semibold);
  font-weight: 600;
  color: var(--color-text);
  line-height: 1.1;
}
.quote-task-tile-currency {
  font-size: 24px;
  margin-right: 2px;
  font-weight: 600;
  vertical-align: baseline;
}
.quote-task-tile-dollars {
  font-size: 24px;
  font-weight: 600;
  vertical-align: baseline;
}
.quote-task-tile-cents {
  font-size: 14px;
  vertical-align: super;
  margin-left: 1px;
  font-weight: 600;
  color: var(--color-text);
  position: relative;
  top: -0.12em;
}
.quote-task-tile-inc {
  font-family: var(--font-regular);
  font-size: 14px;
  line-height: 18px;
  color: var(--color-text);
  margin-top: 4px;
}
.quote-task-tile-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex-shrink: 0;
  margin: 0 0 0 auto !important;
  width: 64px;
  height: 64px;
  min-height: 64px;
  padding: 0 !important;
  border-radius: 50% !important;
  cursor: pointer;
  border: 1px solid var(--color-border-subtle) !important;
  box-shadow: 0 2px 4px rgba(50, 60, 71, 0.1) !important;
  background: var(--color-bg-surface) !important;
  color: #4da1ff !important;
  text-align: center;
}
.quote-task-tile-btn svg {
  display: block;
  flex-shrink: 0;
}
.quote-task-tile-btn:hover {
  background: var(--color-bg-elevated) !important;
  box-shadow: 0 2px 6px rgba(50, 60, 71, 0.14) !important;
}
.quote-task-tile-btn:focus {
  outline: 2px solid rgba(77, 161, 255, 0.45);
  outline-offset: 1px;
}
@media only screen and (max-width: 519px) {
  .quote-task-tile {
    flex-direction: row;
    align-items: stretch;
  }
  .quote-task-tile-image {
    width: 112px;
    min-width: 112px;
    height: auto;
    min-height: 0;
  }
  .quote-task-tile-image-frame {
    min-height: 100%;
  }
  .quote-task-tile-divider {
    width: 1px;
    height: auto;
    align-self: stretch;
  }
  .quote-task-tile-body {
    flex: 1 1 auto;
    min-width: 0;
    padding: 12px 14px;
  }
  .quote-task-tile-code {
    margin-bottom: 4px;
  }
  .quote-task-tile-title {
    font-size: 14px !important;
    line-height: 18px !important;
    margin-bottom: 8px !important;
    flex: 1 1 auto;
  }
  .quote-task-tile-currency,
  .quote-task-tile-dollars {
    font-size: 24px;
  }
  .quote-task-tile-cents {
    font-size: 14px;
  }
  .quote-task-tile-inc {
    font-size: 12px;
    line-height: 16px;
    margin-top: 2px;
  }
  .quote-task-tile-btn {
    width: 64px;
    height: 64px;
    min-height: 64px;
    padding: 0 !important;
  }
}
</style>
